import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";

const MOODS = ["Peaceful", "Grateful", "Seeking", "Burdened", "Hopeful", "Convicted"];
const JOURNAL_IMG = "https://images.pexels.com/photos/5149241/pexels-photo-5149241.jpeg";

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [mood, setMood] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/journal").then((r) => setEntries(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!content.trim()) { toast.error("Write a reflection first."); return; }
    setBusy(true);
    try {
      await api.post("/journal", { title, content, mood, tags: [] });
      setOpen(false); setTitle(""); setContent(""); setMood(null);
      toast.success("Reflection saved.");
      load();
    } catch { toast.error("Could not save."); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    await api.delete(`/journal/${id}`);
    setEntries((e) => e.filter((x) => x.id !== id));
    toast.success("Reflection removed.");
  };

  const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-16">
      <div className="flex items-end justify-between animate-fade-up">
        <div>
          <p className="label-overline mb-2">Reflections</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-none">Journal</h1>
        </div>
        <button data-testid="new-journal-button" onClick={() => setOpen(true)}
          className="flex items-center gap-2 bg-primary text-primary-foreground rounded-sm px-5 py-3 hover:bg-primary/90 transition-colors">
          <Plus className="w-4 h-4" /> New entry
        </button>
      </div>

      {open && (
        <div className="bg-card border border-border rounded-md p-8 mt-10 animate-fade-up" data-testid="journal-editor">
          <input data-testid="journal-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)"
            className="w-full bg-transparent font-heading text-3xl tracking-tight focus:outline-none placeholder:text-muted-foreground/50" />
          <textarea data-testid="journal-content-input" value={content} onChange={(e) => setContent(e.target.value)} rows={8} placeholder="What is God placing on your heart?"
            className="w-full bg-transparent mt-6 text-lg leading-loose resize-none focus:outline-none placeholder:text-muted-foreground/50" />
          <div className="flex flex-wrap gap-2 mt-4">
            {MOODS.map((m) => (
              <button key={m} data-testid={`mood-${m.toLowerCase()}`} onClick={() => setMood(mood === m ? null : m)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${mood === m ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-muted"}`}>
                {m}
              </button>
            ))}
          </div>
          <div className="flex justify-end gap-3 mt-8">
            <button data-testid="journal-cancel-button" onClick={() => setOpen(false)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground px-4 py-2"><X className="w-4 h-4" /> Cancel</button>
            <button data-testid="journal-save-button" onClick={save} disabled={busy} className="bg-primary text-primary-foreground rounded-sm px-6 py-2.5 hover:bg-primary/90 transition-colors disabled:opacity-60">{busy ? "Saving…" : "Save reflection"}</button>
          </div>
        </div>
      )}

      <div className="mt-12 space-y-5">
        {entries.length === 0 && !open && (
          <div className="text-center py-16 animate-fade-up">
            <img src={JOURNAL_IMG} alt="Open journal" className="w-40 h-40 object-cover rounded-md mx-auto opacity-80 grayscale-[30%]" />
            <p className="font-heading text-2xl mt-8">Your journal is a quiet place</p>
            <p className="text-muted-foreground text-sm mt-2">Begin by recording what the Spirit stirs within you.</p>
          </div>
        )}
        {entries.map((e, i) => (
          <article key={e.id} data-testid={`journal-entry-${e.id}`} className="group bg-card border border-border rounded-md p-7 animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="label-overline">{fmt(e.created_at)}{e.mood ? ` · ${e.mood}` : ""}</p>
                <h3 className="font-heading text-2xl tracking-tight mt-1">{e.title}</h3>
              </div>
              <button data-testid={`delete-journal-${e.id}`} onClick={() => remove(e.id)} className="text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <p className="mt-4 leading-loose whitespace-pre-wrap text-foreground/90">{e.content}</p>
          </article>
        ))}
      </div>
    </div>
  );
}
